import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import QuestionCard from './components/QuestionCard';
import ThankYou from './components/ThankYou';
import { getSurveyQuestions, trackSurveyEvent, submitSurvey } from './api/surveyApi';
import { Send, Loader2, Sparkles, AlertCircle } from 'lucide-react';

// Helper to get or generate anonymous ID
const getAnonymousId = () => {
  let id = localStorage.getItem('adium_survey_anon_id');
  if (!id) {
    id = 'anon_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
    localStorage.setItem('adium_survey_anon_id', id);
  }
  return id;
};

// Helper to group questions sharing the same text and having a speaker
const groupQuestions = (rawQuestions) => {
  const steps = [];
  const processedIds = new Set();

  rawQuestions.forEach((q) => {
    if (processedIds.has(q.id)) return;

    const hasSpeaker = Boolean(q.speaker_id || q.speaker_name);

    if (hasSpeaker) {
      const sameTextQuestions = rawQuestions.filter(
        (other) =>
          !processedIds.has(other.id) &&
          other.text.trim().toLowerCase() === q.text.trim().toLowerCase() &&
          Boolean(other.speaker_id || other.speaker_name)
      );

      if (sameTextQuestions.length > 1) {
        sameTextQuestions.forEach((item) => processedIds.add(item.id));
        steps.push({
          isGrouped: true,
          groupId: `group_${q.id}`,
          text: q.text,
          required: sameTextQuestions.some((item) => item.required),
          questions: sameTextQuestions
        });
        return;
      }
    }

    processedIds.add(q.id);
    steps.push({
      isGrouped: false,
      ...q
    });
  });

  return steps;
};

// Helper to check if a single answer is non-empty
const isAnswerGiven = (ans) => {
  if (ans === undefined || ans === null || ans === '') return false;
  if (Array.isArray(ans) && ans.length === 0) return false;
  return true;
};

function App() {
  const [anonymousId] = useState(getAnonymousId);
  const [rawQuestions, setRawQuestions] = useState([]);
  const [steps, setSteps] = useState([]);
  const [answers, setAnswers] = useState({});
  const [extraAnswers, setExtraAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState('');
  const [fetchError, setFetchError] = useState('');

  // Load active survey questions
  useEffect(() => {
    const loadQuestions = async () => {
      setLoading(true);
      setFetchError('');
      try {
        const data = await getSurveyQuestions();
        if (Array.isArray(data)) {
          setRawQuestions(data);
          const groupedSteps = groupQuestions(data);
          setSteps(groupedSteps);

          if (groupedSteps.length > 0) {
            trackSurveyEvent(anonymousId, null, 'entry');
          }
        } else {
          setFetchError('No se pudieron cargar las preguntas.');
        }
      } catch (err) {
        console.error('Failed to load questions:', err);
        setFetchError('Ocurrió un error al conectar con el servidor. Por favor, intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [anonymousId]);

  const handleAnswerChange = (questionId, val, stepKey) => {
    if (stepKey && errors[stepKey]) {
      setErrors(prev => ({ ...prev, [stepKey]: '' }));
    }
    setGlobalError('');
    setAnswers(prev => ({
      ...prev,
      [questionId]: val
    }));
  };

  const handleExtraTextChange = (questionId, txt) => {
    setExtraAnswers(prev => ({
      ...prev,
      [questionId]: txt
    }));
  };

  // Calculate completed steps for progress bar
  const getCompletedStepsCount = () => {
    let count = 0;
    steps.forEach((step) => {
      if (step.isGrouped) {
        const allAnswered = step.questions.every((sq) => isAnswerGiven(answers[sq.id]));
        if (allAnswered) count++;
      } else {
        if (isAnswerGiven(answers[step.id])) count++;
      }
    });
    return count;
  };

  const validateAllSteps = () => {
    const newErrors = {};
    let firstErrorStepId = null;

    steps.forEach((step) => {
      const stepKey = step.groupId || `step_${step.id}`;

      if (step.isGrouped) {
        if (step.required) {
          const missing = step.questions.filter((sq) => !isAnswerGiven(answers[sq.id]));
          if (missing.length > 0) {
            newErrors[stepKey] = `Por favor, evalúa a todos los conferencistas de esta sección (${missing.length} pendiente${missing.length > 1 ? 's' : ''}).`;
            if (!firstErrorStepId) firstErrorStepId = stepKey;
          }
        }
      } else {
        if (step.required && !isAnswerGiven(answers[step.id])) {
          newErrors[stepKey] = 'Esta pregunta es obligatoria.';
          if (!firstErrorStepId) firstErrorStepId = stepKey;
        }
      }
    });

    setErrors(newErrors);

    if (firstErrorStepId) {
      setGlobalError('Por favor, responda todas las preguntas obligatorias antes de enviar la encuesta.');
      const el = document.getElementById(firstErrorStepId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setGlobalError('');

    if (!validateAllSteps()) return;

    setSubmitting(true);

    try {
      const formattedAnswers = rawQuestions.map(q => {
        const ansVal = answers[q.id];
        const extraVal = extraAnswers[q.id];

        let answerString = '';
        if (Array.isArray(ansVal)) {
          answerString = ansVal.join(', ');
        } else if (ansVal !== undefined && ansVal !== null) {
          answerString = ansVal.toString();
        }

        if (q.is_mixed && extraVal) {
          answerString += answerString ? ` | Observación: ${extraVal}` : extraVal;
        }

        return {
          question_id: q.id,
          text: answerString,
          answer_type: q.type
        };
      }).filter(a => a.text.trim() !== '');

      await submitSurvey(anonymousId, formattedAnswers);
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error submitting survey:', err);
      setGlobalError('Hubo un error al enviar sus respuestas. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans text-slate-800 antialiased">
      <Header
        completedCount={getCompletedStepsCount()}
        totalSteps={steps.length}
        isSubmitted={isSubmitted}
      />

      <main className="flex-grow flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-3xl">

          {loading ? (
            <div className="card-survey text-center py-16 flex flex-col items-center justify-center">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
              <p className="text-slate-600 font-semibold">Cargando preguntas de la encuesta...</p>
            </div>
          ) : fetchError ? (
            <div className="card-survey text-center py-12 px-6">
              <div className="w-16 h-16 bg-red-100 text-adium-red rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">
                !
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">No se pudo cargar la encuesta</h3>
              <p className="text-slate-600 text-sm mb-6 max-w-md mx-auto">{fetchError}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn-adium mx-auto"
              >
                Reintentar
              </button>
            </div>
          ) : isSubmitted ? (
            <ThankYou />
          ) : steps.length === 0 ? (
            <div className="card-survey text-center py-12 px-6">
              <Sparkles className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-800 mb-2">Encuesta no disponible</h3>
              <p className="text-slate-600 text-sm">No hay preguntas activas registradas en este momento.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">

              {/* Question List Header Banner */}
              <div className="bg-blue-900 text-white p-6 rounded-2xl shadow-md text-center">
                <h1 className="text-xl sm:text-2xl font-black uppercase tracking-wide">
                  Encuesta de Satisfacción
                </h1>
                <p className="text-xs sm:text-sm text-blue-200 mt-1 font-medium">
                  Por favor, responda a continuación todas las preguntas activas.
                </p>
              </div>

              {/* LIST OF QUESTIONS */}
              <div className="space-y-6">
                {steps.map((step, idx) => {
                  const stepKey = step.groupId || `step_${step.id}`;
                  return (
                    <div key={stepKey} id={stepKey} className="scroll-mt-28">
                      <div className="text-xs font-extrabold uppercase text-blue-700 tracking-wider mb-2 px-2">
                        Pregunta {idx + 1} de {steps.length}
                      </div>
                      <QuestionCard
                        step={step}
                        answers={answers}
                        onAnswerChange={(qId, val) => handleAnswerChange(qId, val, stepKey)}
                        extraAnswers={extraAnswers}
                        onExtraTextChange={handleExtraTextChange}
                        disabled={submitting}
                        error={errors[stepKey]}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Global Error Banner */}
              {globalError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-semibold flex items-center gap-3 animate-fadeIn">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{globalError}</span>
                </div>
              )}

              {/* Single Submit Button for List View */}
              <div className="pt-4 pb-8 flex justify-center">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-adium w-full sm:w-auto px-10 py-4 text-base bg-gradient-to-r from-blue-700 to-indigo-800 shadow-xl cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Enviando Encuesta...</span>
                    </>
                  ) : (
                    <>
                      <span>Enviar Encuesta</span>
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>

            </form>
          )}

        </div>
      </main>

      <Footer />
    </div>
  );
}

export default App;
